// Time zone helpers for scheduled items authored in the admin.
//
// The stored instant stays a UTC "Z" string. A separate IANA name records the
// clock the author set the time on, for example "Asia/Makassar". Two Firestore
// range queries compare startTime as a string, so an offset suffix would sort
// by wall clock and return the wrong rows. The name is kept beside the time
// instead.
//
// The date pickers hand back a JS Date in the browser zone. The author means
// the wall time they typed, on the zone they picked. These helpers move a wall
// time into the right instant and back again.
//
// Nothing here throws on an unknown zone name. Every entry point degrades to
// the browser zone, which is what the form did before the field existed.

const MINUTE_MS = 60 * 1000;

// The browser's own zone, or "UTC" when the runtime will not say.
export const guessTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// Every IANA zone the runtime knows, with UTC and the browser's own zone
// always present. Some runtimes leave UTC out of supportedValuesOf, and an
// author who runs on UTC must still be able to pick it. Older runtimes have no
// supportedValuesOf at all, so they get the short list on its own.
export const listTimeZones = (): string[] => {
  let zones: string[] = [];
  try {
    const supported = (Intl as any).supportedValuesOf;
    if (typeof supported === "function") {
      const values = supported.call(Intl, "timeZone");
      if (Array.isArray(values)) zones = values.filter((z) => typeof z === "string");
    }
  } catch {
    // Fall through to the short list.
  }
  const seen = new Set(zones);
  [guessTimeZone(), "UTC"].forEach((zone) => {
    if (zone && !seen.has(zone)) {
      seen.add(zone);
      zones.push(zone);
    }
  });
  return zones.sort((a, b) => a.localeCompare(b));
};

// True when the runtime accepts the name. A bad name makes Intl throw, so this
// is the one place that test happens.
export const isKnownTimeZone = (timeZone?: string | null): boolean => {
  const name = String(timeZone || "").trim();
  if (!name) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: name }).format(0);
    return true;
  } catch {
    return false;
  }
};

type WallTime = {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
};

// The wall-clock reading of an instant in one zone. Returns null when the zone
// name is unknown, so a caller can fall back instead of throwing.
const wallTimeInZone = (ms: number, timeZone: string): WallTime | null => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts: Record<string, string> = {};
    formatter.formatToParts(new Date(ms)).forEach((part) => {
      parts[part.type] = part.value;
    });
    // Some runtimes render midnight as hour 24 under hour12:false.
    const hour = Number(parts.hour) % 24;
    const wall = {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour,
      minute: Number(parts.minute),
      second: Number(parts.second),
    };
    const valid = Object.keys(wall).every((key) =>
      Number.isFinite((wall as any)[key])
    );
    return valid ? wall : null;
  } catch {
    return null;
  }
};

const wallAsUtcMs = (wall: WallTime): number =>
  Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second
  );

// The zone's offset from UTC at one instant, in minutes. East of Greenwich is
// positive. Returns null for an unknown zone.
export const zoneOffsetMinutes = (
  ms: number,
  timeZone: string
): number | null => {
  const wall = wallTimeInZone(ms, timeZone);
  if (!wall) return null;
  return Math.round((wallAsUtcMs(wall) - ms) / MINUTE_MS);
};

// Take the wall-clock fields of a Date (as the picker set them, in the browser
// zone) and return the instant when that same wall time happens in timeZone.
//
// Daylight saving: the offset is not known before the instant is, so the
// candidate is corrected twice. Two passes converge for every real zone, since
// a correction moves at most a couple of hours and a second transition never
// sits that close.
//
// A wall time that does not exist (the hour a spring-forward skips) has no
// right answer. The second pass applies the post-jump offset, so the result is
// one of the two instants next to the gap. Which one follows the sign of the
// zone's offset: New York stores 02:30 on a skipped day as the instant that
// reads 01:30, and Sydney stores it as the one that reads 03:30. A wall time
// that happens twice (the hour a fall-back repeats) settles on one of the two
// the same way. Every case gives a single, valid instant, so a saved event is
// never ambiguous to a reader. An author who cares picks a different hour.
//
// An unknown or missing zone keeps the old behavior: the browser's own instant.
export const zonedWallTimeToUtcIso = (
  date: Date | null | undefined,
  timeZone?: string | null
): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const name = String(timeZone || "").trim();
  if (!name || !isKnownTimeZone(name)) return date.toISOString();

  const wanted: WallTime = {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  };
  const wantedUtcMs = wallAsUtcMs(wanted);

  let candidate = wantedUtcMs;
  for (let pass = 0; pass < 2; pass += 1) {
    const offset = zoneOffsetMinutes(candidate, name);
    if (offset === null) return date.toISOString();
    const next = wantedUtcMs - offset * MINUTE_MS;
    if (next === candidate) break;
    candidate = next;
  }
  return new Date(candidate).toISOString();
};

// The reverse: read a stored instant as the wall time in timeZone, and hand
// back a Date carrying those fields in the browser zone. The picker then shows
// the author their own clock, not the editor's.
//
// An unknown or missing zone returns the plain Date, today's behavior.
export const utcIsoToZonedWallTime = (
  value: unknown,
  timeZone?: string | null
): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  const ms =
    value instanceof Date ? value.getTime() : new Date(value as any).getTime();
  if (!Number.isFinite(ms)) return null;

  const name = String(timeZone || "").trim();
  if (!name || !isKnownTimeZone(name)) return new Date(ms);

  const wall = wallTimeInZone(ms, name);
  if (!wall) return new Date(ms);
  return new Date(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    0
  );
};
